import { Component, computed, inject } from '@angular/core';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { PermissionStore } from '../../services/permission-service/permission.store';
import { MENU_UI } from '../../constants/MenuKeys';
import { AuthService } from '../../services/auth-service/auth.service';

export interface SidebarMenuItem {
  menuKey: string;
  menuName: string;
  route?: string;
  icon: string;
  isParent: boolean;
  children: SidebarMenuItem[];
}

@Component({
  selector: 'app-menu-sidebar',
  standalone: false,
  templateUrl: './menu-sidebar.html',
  styleUrl: './menu-sidebar.scss'
})
export class MenuSidebar {
  /** Parent menuKey of the open submenu. */
  activeSubmenu: string | null = null;

  private readonly permissionStore = inject(PermissionStore);
  private readonly authService = inject(AuthService);

  /** Sidebar tree from the signed-in role's permissions: API order, names and visibility; route/icon from MENU_UI. */
  readonly menuTree = computed<SidebarMenuItem[]>(() => {
    const viewable = this.permissionStore.menus().filter((m) => m.isView);
    const visible = viewable.filter((m) => MENU_UI[m.menuKey]);
    const toItem = (menuKey: string, menuName: string, isParent: boolean, children: SidebarMenuItem[] = []): SidebarMenuItem => ({
      menuKey,
      menuName,
      route: MENU_UI[menuKey].route,
      icon: MENU_UI[menuKey].icon,
      isParent,
      children,
    });
    const routedChildren = (parentId: number): SidebarMenuItem[] =>
      visible
        .filter((c) => c.parentMenuId === parentId && !!MENU_UI[c.menuKey].route)
        .map((c) => toItem(c.menuKey, c.menuName, false));

    return viewable
      .filter((m) => m.parentMenuId === null)
      .flatMap((m) => {
        // Permission-only group (no MENU_UI entry, e.g. VaultX Binder Management): its pages stay top-level items
        if (!MENU_UI[m.menuKey]) {
          return m.isParent ? routedChildren(m.menuId) : [];
        }
        return [toItem(m.menuKey, m.menuName, m.isParent, routedChildren(m.menuId))];
      })
      .filter((item) => (item.isParent ? item.children.length > 0 : !!item.route));
  });

  constructor(readonly router: Router) {}

  ngOnInit() {
    if (this.authService.isLoggedIn()) {
      this.syncActiveSubmenu(this.router.url);
    }

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.syncActiveSubmenu(event.urlAfterRedirects || event.url);
      }

      if (event instanceof NavigationError) {
        this.activeSubmenu = null;
      }
    });
  }

  toggleSubMenu(menuKey: string) {
    this.activeSubmenu = this.activeSubmenu === menuKey ? null : menuKey;
  }

  /** Exact match for list routes that have sibling routes under the same prefix. */
  isExactRoute(item: SidebarMenuItem): boolean {
    return item.route === '/admin-user' || item.route === '/app-user'
      || item.route === '/admin-user/archive-admin-user' || item.route === '/app-user/archive-app-user'
      || item.route === '/app-user/anonymous-app-user';
  }

  /** App Users stays active on its binders/cards detail pages but not on the archive/anonymous lists. */
  isAppUsersActive(): boolean {
    const url = this.router.url;
    return url.startsWith('/app-user') && !url.includes('/app-user/archive-app-user') && !url.includes('/app-user/anonymous-app-user');
  }

  private syncActiveSubmenu(url: string) {
    const parent = this.menuTree().find((item) =>
      item.children.some((child) => child.route && url.startsWith(this.routePrefix(child.route)))
    );
    this.activeSubmenu = parent?.menuKey ?? null;
  }

  /** '/card-management/sets' -> '/card-management', '/admin-user' -> '/admin-user'. */
  private routePrefix(route: string): string {
    const firstSegment = route.split('/').filter(Boolean)[0];
    return firstSegment ? `/${firstSegment}` : route;
  }

  ngAfterViewInit() {
    if (typeof (window as any).initSidebarScript === 'function') {
      (window as any).initSidebarScript();
    } else {
      setTimeout(() => {
        if ((window as any).initSidebarScript) {
          (window as any).initSidebarScript();
        }
      }, 500);
    }
  }
}
